import { BRAND_PRIMARY } from "@constants/brand.colors";
import { MUI_MENU_ITEM, MUI_TAB, MUI_TOOLTIP } from "@constants/mui.constants";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type { ReactNode } from "react";

const theme = createTheme({
	palette: {
		primary: {
			main: BRAND_PRIMARY,
		},
	},
	typography: {
		fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		// Set font family for all typography variants
		h1: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		h2: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		h3: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		h4: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		h5: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		h6: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		body1: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		body2: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		button: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		caption: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
		overline: {
			fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
		},
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				'*': {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
					"&.Mui-selected": {
						backgroundColor: MUI_MENU_ITEM.SELECTED.BACKGROUND_COLOR,
						color: MUI_MENU_ITEM.SELECTED.COLOR,
					},
					"&.Mui-selected:hover": {
						backgroundColor: MUI_MENU_ITEM.SELECTED.HOVER_COLOR,
						color: MUI_MENU_ITEM.SELECTED.COLOR,
					},
					"&:hover": {
						backgroundColor: MUI_MENU_ITEM.UNSELECTED.HOVER_COLOR,
						color: MUI_MENU_ITEM.SELECTED.COLOR,
					},
				},
			},
		},
		MuiSvgIcon: {
			styleOverrides: {
				root: {},
			},
		},
		MuiTooltip: {
			styleOverrides: {
				tooltip: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
					background: MUI_TOOLTIP.TOOLTIP_BACKGROUND_COLOR,
					color: MUI_TOOLTIP.TOOLTIP_COLOR,
					boxShadow: MUI_TOOLTIP.TOOLTIP_BOX_SHADOW,
					borderRadius: MUI_TOOLTIP.TOOLTIP_BORDER_RADIUS,
					fontSize: MUI_TOOLTIP.TOOLTIP_FONT_SIZE,
					padding: MUI_TOOLTIP.TOOLTIP_PADDING,
					letterSpacing: MUI_TOOLTIP.TOOLTIP_LETTER_SPACING,
				},
				arrow: {
					color: MUI_TOOLTIP.TOOLTIP_BACKGROUND_COLOR,
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
					"& .MuiTabs-indicator": {
						backgroundColor: MUI_TAB.SELECTED.INDICATOR_COLOR,
						height: 4,
						borderRadius: 2,
					},
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
					textTransform: "capitalize",
					fontSize: 12,
					borderRadius: 8,
					minHeight: 40,
					marginRight: 8,
					transition: "background 0.2s, color 0.2s",
					"&.Mui-selected": {
						color: MUI_TAB.SELECTED.TAB_NAME_COLOR,
						backgroundColor: MUI_TAB.SELECTED.BACKGROUND_COLOR,
					},
					"&.Mui-selected:hover": {
						backgroundColor: MUI_TAB.SELECTED.HOVER_COLOR,
					},
					"&:hover": {
						backgroundColor: MUI_TAB.SELECTED.HOVER_COLOR,
					},
				},
			},
		},
		// Add font family to common components
		MuiButton: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiTextField: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiChip: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiCard: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiAccordionDetails: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiAlert: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiSkeleton: {
			styleOverrides: {
				root: {
					fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
				},
			},
		},
		MuiCircularProgress: {
			defaultProps: {
				color: "primary",
			},
		},
	},
});

export function MUIThemeProvider({ children }: { children: ReactNode }) {
	return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
